// src/features/notifications/context/NotificationProvider.tsx

import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { notificationKeys } from '../hooks/useNotifications';

type RealtimeNotificationPayload = {
  id?: string;
  title: string;
  message?: string;
  link?: string;
  type?: string;
  metadata?: Record<string, any>;
};

type NotificationToast = {
  localId: string;
  title: string;
  message?: string;
  link?: string;
  type?: string;
};

const TOAST_MAX_STACK = 3;
const TOAST_AUTO_DISMISS_MS = 7000;

/**
 * Connects to the /notifications WebSocket namespace and invalidates
 * React Query caches on new notification events. Also triggers a
 * browser desktop notification if permission is granted.
 */
export default function NotificationProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const toastTimersRef = useRef<Map<string, number>>(new Map());
  const [toasts, setToasts] = useState<NotificationToast[]>([]);

  const dismissToast = useCallback((toastId: string) => {
    const existingTimer = toastTimersRef.current.get(toastId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      toastTimersRef.current.delete(toastId);
    }
    setToasts((current) => current.filter((toast) => toast.localId !== toastId));
  }, []);

  const addToast = useCallback((data: RealtimeNotificationPayload) => {
    const localId = data.id
      ? `notification:${data.id}`
      : `ephemeral:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

    setToasts((current) => {
      if (data.id && current.some((toast) => toast.localId === localId)) {
        return current;
      }

      return [
        {
          localId,
          title: data.title,
          message: data.message,
          link: data.link,
          type: data.type,
        },
        ...current,
      ].slice(0, TOAST_MAX_STACK);
    });

    const existingTimer = toastTimersRef.current.get(localId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }
    const timeoutId = window.setTimeout(() => dismissToast(localId), TOAST_AUTO_DISMISS_MS);
    toastTimersRef.current.set(localId, timeoutId);
  }, [dismissToast]);

  const handleToastOpen = useCallback((toast: NotificationToast) => {
    if (toast.link) {
      window.location.href = toast.link;
    }
    dismissToast(toast.localId);
  }, [dismissToast]);

  const toastAccentClass = useCallback((type?: string) => {
    switch (type) {
      case 'task_overdue':
      case 'matter_deleted':
        return 'bg-red-100 text-red-700';
      case 'task_completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'task_assigned':
      case 'matter_created':
      case 'matter_document_uploaded':
        return 'bg-blue-100 text-blue-700';
      case 'matter_status_changed':
      case 'matter_transferred':
        return 'bg-purple-100 text-purple-700';
      case 'matter_archived':
        return 'bg-gray-100 text-gray-700';
      case 'task_reminder':
      default:
        return 'bg-amber-100 text-amber-700';
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('webelieve_access_token');
    if (!token) return;

    const socket = io('/notifications', {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('notification:new', (data: RealtimeNotificationPayload) => {
      // Invalidate queries so the bell badge + dropdown refresh
      qc.invalidateQueries({ queryKey: notificationKeys.all });
      addToast(data);

      // Desktop notification — for agent triggers, respect the per-trigger preference;
      // for all other notification types, always show if permission is granted.
      const showDesktop = data.metadata?.showDesktopNotification !== false;
      if (showDesktop && 'Notification' in window && window.Notification.permission === 'granted') {
        const n = new window.Notification(data.title, {
          body: data.message,
          icon: '/favicon.ico',
          tag: 'webelieve-notification',
        });
        if (data.link) {
          n.onclick = () => {
            window.focus();
            window.location.href = data.link!;
          };
        }
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      toastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      toastTimersRef.current.clear();
    };
  }, [addToast, qc]);

  // Request desktop notification permission once
  useEffect(() => {
    if ('Notification' in window && window.Notification.permission === 'default') {
      window.Notification.requestPermission();
    }
  }, []);

  return (
    <>
      {children}
      <div className="pointer-events-none fixed right-4 top-20 z-[9999] flex w-[min(92vw,24rem)] flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.localId}
            className="pointer-events-auto rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-xl"
          >
            <div className="flex items-start gap-3 p-3">
              <div className={`mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${toastAccentClass(toast.type)}`}>
                <BellIcon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[var(--brand-text-primary)]">{toast.title}</p>
                {toast.message && (
                  <p className="mt-1 text-xs text-[var(--brand-text-secondary)]">{toast.message}</p>
                )}
                {toast.link && (
                  <button
                    onClick={() => handleToastOpen(toast)}
                    className="mt-2 text-xs font-medium text-[var(--brand-accent-primary)] hover:text-[var(--brand-accent-primary-hover)]"
                  >
                    Open
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissToast(toast.localId)}
                className="rounded p-1 text-[var(--brand-text-tertiary)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-primary)]"
                aria-label="Dismiss notification"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
